/**
 * Gallery Service
 * 
 * Handles all database operations for gallery categories and posts.
 * Uses Supabase client for CRUD operations and real-time updates.
 */

import { supabase, handleSupabaseError } from '../config/supabaseClient';

/**
 * Fetch all categories ordered by display_order
 * @returns {Promise<{data: Array, error: Object|null}>}
 */
export const fetchCategories = async () => {
    try {
        const { data, error } = await supabase
            .from('categories')
            .select('*')
            .order('display_order', { ascending: true });

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Add a new category
 * @param {string} name - Category name
 * @returns {Promise<{data: Object, error: Object|null}>}
 */
export const addCategory = async (name) => {
    try {
        // Get max display_order to append to end
        const { data: maxData } = await supabase
            .from('categories')
            .select('display_order')
            .order('display_order', { ascending: false })
            .limit(1);

        const nextOrder = maxData && maxData.length > 0 ? maxData[0].display_order + 1 : 0;

        const { data, error } = await supabase
            .from('categories')
            .insert([{ name, display_order: nextOrder }])
            .select()
            .single();

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Update a category
 * @param {string} id - Category UUID
 * @param {Object} updates - Fields to update (e.g., {name: "New Name"})
 * @returns {Promise<{data: Object, error: Object|null}>}
 */
export const updateCategory = async (id, updates) => {
    try {
        const { data, error } = await supabase
            .from('categories')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Delete a category (cascade deletes posts)
 * @param {string} id - Category UUID
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const deleteCategory = async (id) => {
    try {
        const { error } = await supabase
            .from('categories')
            .delete()
            .eq('id', id);

        if (error) return { success: false, error: handleSupabaseError(error) };
        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Fetch posts for a specific category, ordered by display_order
 * @param {string} categoryId - Category UUID
 * @returns {Promise<{data: Array, error: Object|null}>}
 */
export const fetchPostsByCategory = async (categoryId) => {
    try {
        const { data, error } = await supabase
            .from('posts')
            .select('*')
            .eq('category_id', categoryId)
            .order('display_order', { ascending: true });

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Fetch all posts across all categories
 * @returns {Promise<{data: Array, error: Object|null}>}
 */
export const fetchAllPosts = async () => {
    try {
        const { data, error } = await supabase
            .from('posts')
            .select(`
        *,
        categories (
          id,
          name
        )
      `)
            .order('created_at', { ascending: false });

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Add a new post to a category
 * @param {string} categoryId - Category UUID
 * @param {string} imageUrl - URL to image in Supabase Storage
 * @param {string} caption - Post caption
 * @param {string} date - Date in YYYY-MM-DD format (optional, defaults to today)
 * @returns {Promise<{data: Object, error: Object|null}>}
 */
export const addPost = async (categoryId, imageUrl, caption, date = null) => {
    try {
        // Get max display_order for this category
        const { data: maxData } = await supabase
            .from('posts')
            .select('display_order')
            .eq('category_id', categoryId)
            .order('display_order', { ascending: false })
            .limit(1);

        const nextOrder = maxData && maxData.length > 0 ? maxData[0].display_order + 1 : 0;

        const postDate = date || new Date().toISOString().split('T')[0];

        const { data, error } = await supabase
            .from('posts')
            .insert([{
                category_id: categoryId,
                image_url: imageUrl,
                caption,
                date: postDate,
                display_order: nextOrder,
            }])
            .select()
            .single();

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Update a post
 * @param {string} id - Post UUID
 * @param {Object} updates - Fields to update (e.g., {caption: "New caption"})
 * @returns {Promise<{data: Object, error: Object|null}>}
 */
export const updatePost = async (id, updates) => {
    try {
        const { data, error } = await supabase
            .from('posts')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) return { data: null, error: handleSupabaseError(error) };
        return { data, error: null };
    } catch (err) {
        return { data: null, error: handleSupabaseError(err) };
    }
};

/**
 * Delete a post
 * @param {string} id - Post UUID
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const deletePost = async (id) => {
    try {
        const { error } = await supabase
            .from('posts')
            .delete()
            .eq('id', id);

        if (error) return { success: false, error: handleSupabaseError(error) };
        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Reorder posts within a category
 * @param {string} categoryId - Category UUID
 * @param {Array} postIds - Array of post UUIDs in new order
 * @returns {Promise<{success: boolean, error: Object|null}>}
 */
export const reorderPosts = async (categoryId, postIds) => {
    try {
        // Update display_order for each post
        const updates = postIds.map((postId, index) => ({
            id: postId,
            display_order: index,
        }));

        // Batch update using upsert
        const { error } = await supabase
            .from('posts')
            .upsert(updates, { onConflict: 'id' });

        if (error) return { success: false, error: handleSupabaseError(error) };
        return { success: true, error: null };
    } catch (err) {
        return { success: false, error: handleSupabaseError(err) };
    }
};

/**
 * Subscribe to real-time changes in categories table
 * @param {Function} callback - Function to call when data changes
 * @returns {Object} - Subscription object with unsubscribe method
 */
export const subscribeToCategoryChanges = (callback) => {
    const subscription = supabase
        .channel('categories-changes')
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'categories',
        }, callback)
        .subscribe();

    return subscription;
};

/**
 * Subscribe to real-time changes in posts table
 * @param {Function} callback - Function to call when data changes
 * @returns {Object} - Subscription object with unsubscribe method
 */
export const subscribeToPostChanges = (callback) => {
    const subscription = supabase
        .channel('posts-changes')
        .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'posts',
        }, callback)
        .subscribe();

    return subscription;
};
